import {useCallback, useState} from 'react';

type AccordionType = 'single' | 'multiple';

type UseAccordionStateReturn<T extends AccordionType> = {
  value: T extends 'multiple' ? string[] : string;
  toggle: (itemValue: string) => void;
  open: (itemValue: string) => void;
  close: (itemValue: string) => void;
  closeAll: () => void;
  getOpenItems: () => string[];
};

export function useAccordionState<T extends AccordionType = 'single'>(props?: {
  defaultValue?: T extends 'multiple' ? string[] : string;
  type?: T;
}): UseAccordionStateReturn<T> {
  const type = props?.type ?? 'single';

  const [value, setValue] = useState<string | string[]>(() => {
    if (props?.defaultValue !== undefined) return props.defaultValue;
    return type === 'multiple' ? [] : '';
  });

  const closeAll = useCallback(() => {
    setValue(type === 'multiple' ? [] : '');
  }, [type]);

  const toggle = useCallback(
    (itemValue: string) => {
      setValue(prev => {
        if (type === 'multiple') {
          const current = Array.isArray(prev) ? prev : [];
          return current.includes(itemValue)
            ? current.filter(v => v !== itemValue)
            : [...current, itemValue];
        }

        return prev === itemValue ? '' : itemValue;
      });
    },
    [type],
  );

  const open = useCallback(
    (itemValue: string) => {
      setValue(prev => {
        if (type === 'multiple') {
          const current = Array.isArray(prev) ? prev : [];
          return current.includes(itemValue) ? current : [...current, itemValue];
        }

        return itemValue;
      });
    },
    [type],
  );

  const close = useCallback(
    (itemValue: string) => {
      setValue(prev => {
        if (type === 'multiple') {
          const current = Array.isArray(prev) ? prev : [];
          return current.filter(v => v !== itemValue);
        }

        return prev === itemValue ? '' : prev;
      });
    },
    [type],
  );

  const getOpenItems = useCallback(() => {
    if (type === 'multiple') {
      return Array.isArray(value) ? value : [];
    }

    return value ? [value as string] : [];
  }, [type, value]);

  return {
    value: value as UseAccordionStateReturn<T>['value'],
    toggle,
    open,
    close,
    closeAll,
    getOpenItems,
  };
}
